import React, { useMemo, useEffect, useRef, useContext } from 'react';
import { useIntl, useParams } from 'umi';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import {
  axisLabelFormatter,
  getMetricFormatRule,
  parseChartValue,
  getValueFromData,
} from '@/pages/benchmark/utils/utils';
import { DEFAULT_CHART_SERIES_CONFIG } from '@/pages/benchmark/_config/constant';
import { getAllMetricTooltip } from './tooltip';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { LayoutContext } from '@/pages/benchmark/state/LayoutContext';

const MetricDetailLineChart = (props: any) => {
  const { metricGroupList, metricData, metricConfig, chartConfig } = props || {};

  const { metric } = useParams<{ metric: string; }>();
  const { locale } = useIntl(); // 中英切换方法
  const isEn = locale === LANGUAGE_EN;
  const { data } = metricData || {};
  
  const currentMetricConfig = metricConfig?.[metric] || {};
  // 拿到 echarts 实例用作事件绑定
  const chartRef = useRef<any>(null);
  const { frameType } = useContext(LayoutContext);

  // 获取 series 配置，优先使用传入的 chartConfig，否则使用默认配置
  const seriesConfig = useMemo(() => {
    return chartConfig?.seriesConfig || DEFAULT_CHART_SERIES_CONFIG;
  }, [chartConfig?.seriesConfig]);

  const { xAxisData, series, activeSeriesConfig } = useMemo(() => {
    const xAxisData = metricGroupList?.map((item: any) =>
      getName(isEn, item.group_label_en, item.group_label_cn),
    );

    // 根据配置动态生成 series，并过滤掉没有数据的 series
    const generatedSeries = seriesConfig
      .map((config: any) => {
        const { nameEn, nameCn, dataKey, color, skipFormat } = config;
        
        // 获取原始数据并解析
        const rawData = metricGroupList?.map((item: any) => 
          getValueFromData(data?.[item?.metric], dataKey)
        );
        const parsedData = rawData.map((val: any) => parseChartValue(val));
        
        // 提取数值用于绑定图表
        const seriesData = parsedData.map((p: any) => p.numericValue);
        
        // 检查是否有有效数据（非 undefined）
        const hasValidData = seriesData.some((val: any) => 
          val !== undefined
        );
        
        // 如果没有有效数据，返回 null，后续过滤掉
        if (!hasValidData) {
          return null;
        }
        
        // 检查该 series 是否包含百分比数据
        const hasPercentData = parsedData.some((p: any) => p.isPercent);
        
        return {
          name: getName(isEn, nameEn, nameCn),
          type: 'line',
          data: seriesData,
          dataKey, // 保留 dataKey 用于 tooltip
          skipFormat, // 保留 skipFormat 用于 label 格式化
          hasPercentData, // 标记是否有百分比数据
          parsedData, // 保留解析后的数据用于 label 格式化
          label: {
            show: true,
            position: 'top',
          },
          itemStyle: { color },
        };
      })
      .filter(Boolean); // 过滤掉 null 的 series

    const newSeries = generatedSeries.map((item: any) => ({
      ...item,
      label: {
        show: true,
        position: 'top',
        formatter: (params: any) => {
          const dataIndex = params?.dataIndex;
          const parsedItem = item.parsedData?.[dataIndex];
          
          // 如果配置了 skipFormat，直接返回原始值
          if (item.skipFormat && parsedItem?.originalValue !== undefined) {
            return parsedItem.originalValue;
          }
          
          // 如果原始值是百分比格式，返回原始百分比字符串
          if (parsedItem?.isPercent && parsedItem?.originalValue !== undefined) {
            return parsedItem.originalValue;
          }
          
          const valueFormatRule = getMetricFormatRule(currentMetricConfig);
          // 如果值是文本类型（无法解析为数字），直接返回原始值
          if (parsedItem?.numericValue === undefined && parsedItem?.originalValue !== undefined) {
            return parsedItem.originalValue;
          }
          return dataFormat({ value: params?.value, formatRule: valueFormatRule });
        },
        color: item.itemStyle?.color,
      },
    }));

    // 返回实际生效的 seriesConfig，用于 tooltip
    const activeSeriesConfig = seriesConfig.filter((config: any) => {
      const rawData = metricGroupList?.map((item: any) => 
        getValueFromData(data?.[item?.metric], config.dataKey)
      );
      const parsedData = rawData.map((val: any) => parseChartValue(val));
      return parsedData.some((p: any) => p.numericValue !== undefined);
    });

    return {
      xAxisData,
      series: newSeries,
      activeSeriesConfig,
    };
  }, [metricGroupList, isEn, data, seriesConfig, currentMetricConfig]);

  // 注册点击事件：点击打开对应同组指标详情页
  useEffect(() => {
    const chartInstance = chartRef.current?.instance?.();
    if (chartInstance) {
      const handleClick = (params: any) => {
        if (params.componentType === 'series' && params.seriesType === 'line') {
          const groupLabel = params.name;
          const clickedItem = metricGroupList?.find((item: any) => {
            const label = getName(isEn, item.group_label_en, item.group_label_cn);
            return label === groupLabel;
          });

          if (clickedItem?.metric) {
            const targetMetric = clickedItem.metric;
            // 根据 category 判断跳转路径，IAA 类型跳转 metricIaa 页面
            const targetMetricConfig = metricConfig?.[targetMetric];
            const isIAA = targetMetricConfig?.category === 'IAA';
            const pagePath = isIAA ? 'metricIaa' : 'metricDetail';
            window.open(`/benchmark/${frameType}/explore/${pagePath}/${targetMetric}`);
          }
        }
      };

      chartInstance.on('click', handleClick);
      return () => {
        chartInstance.off('click', handleClick);
      };
    }
  }, [metricGroupList, isEn, frameType, metricConfig]);

  // 检查所有 series 是否包含百分比数据
  const hasPercentDataInSeries = useMemo(() => {
    return series.some((s: any) => s.hasPercentData);
  }, [series]);

  // 如果没有有效的 series 数据，不渲染图表
  if (series.length === 0) {
    return null;
  }

  return (
    <LineChart
      ref={chartRef}
      height={300}
      legendData={series.map((item: any) => item.name)}
      xAxisData={xAxisData}
      series={series}
      formatterFunc={(params: any) =>
        getAllMetricTooltip({
          metricConfig,
          metricGroupList,
          currentMetric: metric,
          isEn,
          series,
          params,
          seriesConfig: activeSeriesConfig, // 传递实际生效的配置
        })
      }
      setOtherConfigFunc={(opt: any) => {
        // y轴格式化
        if (opt?.yAxis?.axisLabel) {
          opt.yAxis.axisLabel.formatter = (value: number) => {
            // 如果数据是百分比格式，直接在数值后加 % 符号
            if (hasPercentDataInSeries) {
              return `${value}%`;
            }
            return axisLabelFormatter(value, currentMetricConfig);
          };
        }
        return opt;
      }}
    />
  );
};
export default MetricDetailLineChart;
